import { useState, useEffect } from 'react';
import { useApiContext } from '@/contexts/apiContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import TokenIcon from '@mui/icons-material/Token';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { startOfWeek, subDays, format } from 'date-fns';

type PresetType = 'thisWeek' | 'today' | '7days' | '30days' | 'custom';

const getLocalDateString = (d: Date = new Date()) => {
  return format(d, 'yyyy-MM-dd');
};

const getStartOfWeekDateString = () => {
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
  return format(monday, 'yyyy-MM-dd');
};

const AnalyticsCard = () => {
  const theme = useTheme();
  const apiContext = useApiContext();

  const [metric, setMetric] = useState<'events' | 'tokens' | 'latency'>('events');
  const [preset, setPreset] = useState<PresetType>('thisWeek');
  const [startDate, setStartDate] = useState(() => getStartOfWeekDateString());
  const [endDate, setEndDate] = useState(() => getLocalDateString());
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<{
    events: { name: string; value: number }[];
    tokens: { name: string; value: number }[];
    latency: { name: string; value: number }[];
  }>({ events: [], tokens: [], latency: [] });
  const [summary, setSummary] = useState({ totalEvents: 0, totalTokens: 0, avgLatencyMs: 0 });

  const applyPreset = (p: PresetType) => {
    setPreset(p);
    const todayStr = getLocalDateString();
    if (p === 'thisWeek') {
      setStartDate(getStartOfWeekDateString());
      setEndDate(todayStr);
    } else if (p === 'today') {
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (p === '7days') {
      setStartDate(format(subDays(new Date(), 6), 'yyyy-MM-dd'));
      setEndDate(todayStr);
    } else if (p === '30days') {
      setStartDate(format(subDays(new Date(), 29), 'yyyy-MM-dd'));
      setEndDate(todayStr);
    }
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      try {
        const metricsData = await apiContext.getUsageMetrics(startDate, endDate);
        setAnalyticsData(metricsData);

        const totalEvents = (metricsData.events || []).reduce((sum, item) => sum + (item.value || 0), 0);
        const totalTokens = (metricsData.tokens || []).reduce((sum, item) => sum + (item.value || 0), 0);
        const latencies = (metricsData.latency || []).filter((item) => item.value > 0);
        const avgLatencyMs =
          latencies.length > 0
            ? Math.round(latencies.reduce((sum, item) => sum + item.value, 0) / latencies.length)
            : 0;

        setSummary({ totalEvents, totalTokens, avgLatencyMs });
      } catch (e) {
        // silently degrade
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, [startDate, endDate]);

  const getMetricDetails = () => {
    switch (metric) {
      case 'events':
        return { label: 'Events Processed', color: '#7c3aed' };
      case 'tokens':
        return { label: 'LLM Tokens', color: '#3b82f6' };
      case 'latency':
        return { label: 'Avg Latency (ms)', color: '#f59e0b' };
    }
  };

  const details = getMetricDetails();
  const chartData = analyticsData[metric] || [];

  const getPresetLabel = () => {
    switch (preset) {
      case 'thisWeek':
        return 'This Week';
      case 'today':
        return 'Today';
      case '7days':
        return '7 Days';
      case '30days':
        return '30 Days';
      case 'custom':
        return 'Custom';
    }
  };

  const inputStyle = {
    fontSize: '10px',
    padding: '2px 4px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    background: 'transparent',
    color: theme.palette.text.secondary,
    outline: 'none',
    fontFamily: 'inherit',
  };

  return (
    <Card
      className="glass-card-light"
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.4)',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 1.5, pb: '8px !important', display: 'flex', flexDirection: 'column' }}>
        {/* Header row */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="nowrap" gap={1} sx={{ mb: 0.75, width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
              Usage Analytics
            </Typography>
            <Chip
              icon={<DateRangeIcon sx={{ fontSize: '10px !important' }} />}
              label={getPresetLabel()}
              size="small"
              sx={{
                height: 18,
                fontSize: '8.5px',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.secondary.main, 0.15),
                color: 'secondary.main',
                '& .MuiChip-icon': { color: 'secondary.main', ml: 0.5, mr: -0.5 },
                px: 0.25,
              }}
            />
          </Stack>

          {/* Date range pickers */}
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            <input
              type="date"
              value={startDate}
              max={endDate || getLocalDateString()}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPreset('custom');
              }}
              style={{ ...inputStyle, width: '88px', fontSize: '9px', padding: '1px 2px' }}
            />
            <Typography variant="caption" sx={{ fontSize: '9px', color: 'text.secondary', fontWeight: 600, px: 0.25 }}>
              -
            </Typography>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={getLocalDateString()}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPreset('custom');
              }}
              style={{ ...inputStyle, width: '88px', fontSize: '9px', padding: '1px 2px' }}
            />
          </Stack>
        </Stack>

        {/* Quick Range Presets row */}
        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
          {(['thisWeek', 'today', '7days', '30days'] as const).map((p) => (
            <Button
              key={p}
              size="small"
              onClick={() => applyPreset(p)}
              variant={preset === p ? 'contained' : 'outlined'}
              color="primary"
              sx={{
                textTransform: 'none',
                px: 0.75,
                py: 0.15,
                minWidth: 0,
                fontSize: '9px',
                borderRadius: '4px',
                boxShadow: 'none',
                fontWeight: preset === p ? 700 : 500,
                height: 20,
                borderColor: preset === p ? 'primary.main' : theme.palette.divider,
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {p === 'thisWeek' ? 'This Week' : p === 'today' ? 'Today' : p === '7days' ? '7 Days' : '30 Days'}
            </Button>
          ))}
        </Stack>

        {/* Live summary stats */}
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flex: 1, p: 0.75, borderRadius: '8px', bgcolor: alpha('#7c3aed', 0.08) }}
          >
            <BoltIcon sx={{ fontSize: 12, color: '#7c3aed' }} />
            <Box>
              <Typography
                variant="caption"
                sx={{ fontSize: '8px', color: 'text.secondary', fontWeight: 700, display: 'block', lineHeight: 1 }}
              >
                Events
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: '11px', fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}
              >
                {summary.totalEvents.toLocaleString()}
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flex: 1, p: 0.75, borderRadius: '8px', bgcolor: alpha('#3b82f6', 0.08) }}
          >
            <TokenIcon sx={{ fontSize: 12, color: '#3b82f6' }} />
            <Box>
              <Typography
                variant="caption"
                sx={{ fontSize: '8px', color: 'text.secondary', fontWeight: 700, display: 'block', lineHeight: 1 }}
              >
                Tokens
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: '11px', fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}
              >
                {summary.totalTokens.toLocaleString()}
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flex: 1, p: 0.75, borderRadius: '8px', bgcolor: alpha('#f59e0b', 0.08) }}
          >
            <AccessTimeIcon sx={{ fontSize: 12, color: '#f59e0b' }} />
            <Box>
              <Typography
                variant="caption"
                sx={{ fontSize: '8px', color: 'text.secondary', fontWeight: 700, display: 'block', lineHeight: 1 }}
              >
                Avg Latency
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: '11px', fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}
              >
                {summary.avgLatencyMs}ms
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Metric toggle */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '9px', fontWeight: 700, color: 'text.secondary' }}>
            {details.label}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {(['events', 'tokens', 'latency'] as const).map((m) => (
              <Button
                key={m}
                size="small"
                onClick={() => setMetric(m)}
                variant={metric === m ? 'contained' : 'text'}
                color="secondary"
                sx={{
                  textTransform: 'none',
                  px: 1,
                  py: 0.25,
                  minWidth: 0,
                  fontSize: '9px',
                  borderRadius: '4px',
                  boxShadow: 'none',
                  fontWeight: 700,
                  bgcolor: metric === m ? 'secondary.main' : 'transparent',
                  color: metric === m ? 'text.primary' : 'text.secondary',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </Button>
            ))}
          </Stack>
        </Stack>

        <Box sx={{ width: '100%', height: 90, mt: 0.5, position: 'relative' }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress size={20} />
            </Box>
          ) : chartData.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px' }}>
                No data for selected date range
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="99%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -30, bottom: 0 }}>
                <defs>
                  <linearGradient id={`color-${metric}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={details.color} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={details.color} stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                <XAxis dataKey="name" tick={{ fontSize: 8 }} stroke="transparent" tickLine={false} />
                <YAxis tick={{ fontSize: 8 }} stroke="transparent" tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: '6px',
                    fontSize: '10px',
                    padding: '4px 8px',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={details.color}
                  strokeWidth={1.5}
                  fillOpacity={1}
                  fill={`url(#color-${metric})`}
                  isAnimationActive={false}
                  connectNulls={true}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AnalyticsCard;
